import { Close, ConfirmationNumber, Delete, LocalActivity } from '@mui/icons-material';
import { Autocomplete, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { api } from '../../../services/api';
import { toast } from 'react-toastify';

const UpdateCajaModal = ({ open, onClose, onEditar, vendedores, allTarifas: tarifasProp, loading, eventoId, cajaId }) => {
  const allTarifas = tarifasProp ?? [];

  const [nombreCaja, setNombreCaja] = useState("");
  const [vendedor, setVendedor] = useState(null);
  const [tarifasSeleccionadas, setTarifasSeleccionadas] = useState([]);
  const [tarifasConStock, setTarifasConStock] = useState([]);
  const [cajaMacro, setCajaMacro] = useState("");
  const [esPreimpresa, setEsPreimpresa] = useState(false);
  const [pasarela, setPasarela] = useState("");
  const [loadingPasarela, setLoadingPasarela] = useState(false);
  const [loadingCaja, setLoadingCaja] = useState(false);

  const obtenerPasarela = async () => {
    setLoadingPasarela(true);
    const res = await api.caja.getPasarelaEvento(eventoId);
    setLoadingPasarela(false);

    if (res.status === "success") {
      setPasarela(res.data.pasarela);
    } else {
      toast.error("Error al detectar pasarela: " + res.message, {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  const obtenerCaja = async () => {
    if (!cajaId) return;

    setLoadingCaja(true);
    const res = await api.caja.getCaja(eventoId, cajaId);
    setLoadingCaja(false);

    if (res.status === "success") {
      const caja = res.data;
      setNombreCaja(caja.caja);
      setCajaMacro(caja.cajamacro || "");
      setEsPreimpresa(caja.preimpreso || false);

      const vendedorEncontrado = vendedores.find(v => v.id === caja.idusuario);
      setVendedor(vendedorEncontrado || null);

      const tarifasConStockData = caja?.tarifas?.map(t => ({
        ...t,
        stock: t.stock
      }));

      setTarifasSeleccionadas(tarifasConStockData);
      setTarifasConStock(tarifasConStockData);
    } else {
      toast.error("Error al obtener caja: " + res.message, {
        position: "top-right",
        autoClose: 4000,
      });
    }
  };

  useEffect(() => {
    if (open && eventoId) {
      obtenerPasarela();
    }
  }, [open, eventoId]);

  useEffect(() => {
    if (open && cajaId && vendedores.length > 0) {
      obtenerCaja();
    }
  }, [open, cajaId, vendedores]);

  const resetForm = () => {
    setNombreCaja("");
    setVendedor(null);
    setTarifasSeleccionadas([]);
    setTarifasConStock([]);
    setCajaMacro("");
    setEsPreimpresa(false);
  };


  const handleTarifasChange = (_, newTarifas) => {
    setTarifasSeleccionadas(newTarifas);

    const nuevasTarifasConStock = newTarifas?.map(tarifa => {
      const existente = tarifasConStock?.find(t => t.id === tarifa.id);
      return {
        ...tarifa,
        stock: existente ? existente.stock : true
      };
    });

    setTarifasConStock(nuevasTarifasConStock);
  };

  const handleStockChange = (tarifaId, checked) => {
    setTarifasConStock(prev =>
      prev?.map(tarifa =>
        tarifa.id === tarifaId
          ? { ...tarifa, stock: checked }
          : tarifa
      )
    );
  };

  const handleRemoveTarifa = (tarifaId) => {
    const nuevasTarifas = tarifasSeleccionadas.filter(t => t.id !== tarifaId);
    setTarifasSeleccionadas(nuevasTarifas);
    setTarifasConStock(prev => prev.filter(t => t.id !== tarifaId));
  };

  const handleSubmit = () => {
    if (!nombreCaja || !vendedor) return;
    if (pasarela === "CLICK" && !cajaMacro) return;

    onEditar({
      caja: nombreCaja,
      idusuario: vendedor.id,
      preimpreso: esPreimpresa,
      tarifas: tarifasConStock?.map(t => ({
        id: t.id,
        stock: t.stock
      })),
      ...(pasarela === "CLICK" && { cajamacro: cajaMacro })
    });

    resetForm();
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const cargarTodosTipoTickets = () => {
    const tickets = allTarifas.filter(t => t.idtipo === 1);
    const tarifasExistentes = [...tarifasSeleccionadas];

    const nuevosTickets = tickets.filter(
      ticket => !tarifasExistentes.some(t => t.id === ticket.id)
    );

    const ticketsConStock = nuevosTickets.map(tarifa => ({
      ...tarifa,
      stock: true
    }));

    setTarifasSeleccionadas([...tarifasExistentes, ...nuevosTickets]);
    setTarifasConStock([...tarifasConStock, ...ticketsConStock]);
  };

  const cargarTodosTipoVouchers = () => {
    const vouchers = allTarifas.filter(t => t.idtipo === 2);

    const tarifasExistentes = [...tarifasSeleccionadas];

    const nuevosVouchers = vouchers.filter(
      voucher => !tarifasExistentes.some(t => t.id === voucher.id)
    );

    const vouchersConStock = nuevosVouchers.map(tarifa => ({
      ...tarifa,
      stock: true
    }));

    setTarifasSeleccionadas([...tarifasExistentes, ...nuevosVouchers]);
    setTarifasConStock([...tarifasConStock, ...vouchersConStock]);
  };

  return (
    <Dialog open={open}
      disableEscapeKeyDown
      maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box sx={{ display: "flex", flexDirection: "row", gap: "10px", alignItems: "center" }}>
            <Typography variant="h5" sx={{ fontFamily: "Bricolage Grotesque", color: "#0b0062" }}>
              Editar caja
            </Typography>
            {pasarela && (
              <Box sx={{
                p: 1,
                backgroundColor: pasarela === "PVS" ? "#e8f5e8" : "#e3f2fd",
                borderRadius: 1,
                border: `1px solid ${pasarela === "PVS" ? "#c8e6c9" : "#bbdefb"}`
              }}>
                <Typography sx={{
                  fontSize: "14px",
                  color: "#666",
                  fontWeight: 500
                }}>
                  <strong style={{
                    color: pasarela === "PVS" ? "#2e7d32" : "#1976d2"
                  }}>{pasarela}</strong>
                </Typography>
              </Box>
            )}
          </Box>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {loadingPasarela || loadingCaja ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2 }}>
            <CircularProgress size={24} />
            <Typography sx={{ ml: 2, color: "#666" }}>
              {loadingPasarela ? "Detectando pasarela..." : "Cargando datos de la caja..."}
            </Typography>
          </Box>
        ) : (
          <>
            <Typography sx={{ mb: 0, color: "#0b0062" }}>
              Nombre de la caja <span style={{ color: "red" }}>*</span>
            </Typography>
            <TextField
              sx={{
                fontFamily: "Bricolage Grotesque",
                mb: 2,
                '& .MuiInputBase-input-MuiOutlinedInput-input': {
                  fontFamily: "Bricolage Grotesque",
                },
                '& label.Mui-focused': {
                  color: '#5656FF',
                  fontFamily: "Bricolage Grotesque"
                },
                '& .MuiOutlinedInput-root': {
                  height: "40px",
                  '& fieldset': {
                    borderColor: '#B2BAC2',
                  },
                  '&:hover fieldset': {
                    borderColor: '#5656ff',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#5656ff',
                  },
                }
              }}
              value={nombreCaja}
              onChange={e => setNombreCaja(e.target.value)}
              fullWidth
              margin="none"
            />

            <Typography sx={{ mb: 0, color: "#0b0062" }}>
              Vendedor <span style={{ color: "red" }}>*</span>
            </Typography>
            <Autocomplete
              sx={{
                fontFamily: "Bricolage Grotesque",
                mb: 2,
                '& .MuiInputBase-input': {
                  fontFamily: "Bricolage Grotesque",
                },
                '& label.Mui-focused': {
                  color: '#5656FF',
                  fontFamily: "Bricolage Grotesque"
                },
                '& .MuiOutlinedInput-root': {
                  minHeight: "40px",
                  '& fieldset': {
                    borderColor: '#B2BAC2',
                  },
                  '&:hover fieldset': {
                    borderColor: '#5656ff',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#5656ff',
                  },
                },
                '& .MuiAutocomplete-inputRoot': {
                  fontFamily: "Bricolage Grotesque",
                  minHeight: "40px",
                  paddingTop: "0px !important",
                  paddingBottom: "0px !important",
                },
                '& .MuiAutocomplete-input': {
                  fontFamily: "Bricolage Grotesque",
                }
              }}
              options={vendedores}
              getOptionLabel={v => `${v.nombre} ${v.apellido} (${v.email})`}
              value={vendedor}
              onChange={(_, val) => setVendedor(val)}
              renderInput={params => <TextField {...params} margin="none" />}
            />

            {pasarela === "CLICK" && (
              <>
                <Typography sx={{ mb: 0, color: "#0b0062" }}>
                  ID Caja Macro <span style={{ color: "red" }}>*</span>
                </Typography>
                <TextField
                  sx={{
                    fontFamily: "Bricolage Grotesque",
                    mb: 2,
                    '& .MuiInputBase-input-MuiOutlinedInput-input': {
                      fontFamily: "Bricolage Grotesque",
                    },
                    '& label.Mui-focused': {
                      color: '#5656FF',
                      fontFamily: "Bricolage Grotesque"
                    },
                    '& .MuiOutlinedInput-root': {
                      height: "40px",
                      '& fieldset': {
                        borderColor: '#B2BAC2',
                      },
                      '&:hover fieldset': {
                        borderColor: '#5656ff',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#5656ff',
                      },
                    }
                  }}
                  value={cajaMacro}
                  onChange={e => setCajaMacro(e.target.value)}
                  placeholder="Ej: C1, C2, C3..."
                  fullWidth
                  margin="none"
                />
              </>
            )}

            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
              <Typography sx={{ color: "#0b0062" }}>Tarifas (opcional)</Typography>

              <Box sx={{ display: "flex", gap: 1 }}>
                <Button
                  size="small"
                  variant="text"
                  startIcon={<ConfirmationNumber />}
                  onClick={cargarTodosTipoTickets}
                  disabled={!allTarifas || allTarifas.filter(t => t.idtipo === 1).length === 0}
                  sx={{
                    fontSize: "11px",
                    textTransform: "none",
                    color: "#5656ff",
                    border: "none",
                    '&:hover': {
                      borderColor: "#5656ff",
                      backgroundColor: "#f0f0ff"
                    }
                  }}
                >
                  Tickets
                </Button>

                <Button
                  size="small"
                  variant="text"
                  startIcon={<LocalActivity />}
                  onClick={cargarTodosTipoVouchers}
                  disabled={!allTarifas || allTarifas.filter(t => t.idtipo === 2).length === 0}
                  sx={{
                    fontSize: "11px",
                    textTransform: "none",
                    color: "#ff6b35",
                    border: "none",
                    '&:hover': {
                      borderColor: "#ff6b35",
                      backgroundColor: "#fff5f2"
                    }
                  }}
                >
                  Vouchers
                </Button>
              </Box>
            </Box>
            <Autocomplete
              sx={{
                fontFamily: "Bricolage Grotesque",
                mb: 2,
                '& .MuiInputBase-input': {
                  fontFamily: "Bricolage Grotesque",
                },
                '& label.Mui-focused': {
                  color: '#5656FF',
                  fontFamily: "Bricolage Grotesque"
                },
                '& .MuiOutlinedInput-root': {
                  minHeight: "40px",
                  '& fieldset': {
                    borderColor: '#B2BAC2',
                  },
                  '&:hover fieldset': {
                    borderColor: '#5656ff',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#5656ff',
                  },
                },
                '& .MuiAutocomplete-inputRoot': {
                  fontFamily: "Bricolage Grotesque",
                  minHeight: "40px",
                  paddingTop: "0px !important",
                  paddingBottom: "0px !important",
                },
                '& .MuiAutocomplete-input': {
                  fontFamily: "Bricolage Grotesque",
                }
              }}
              multiple
              options={allTarifas}
              noOptionsText="No hay tarifas disponibles"
              getOptionLabel={t => `${t.concepto} - $${t.precio}`}
              value={tarifasSeleccionadas}
              onChange={handleTarifasChange}
              renderInput={params => <TextField {...params} margin="none" />}
            />

            {tarifasConStock?.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography sx={{ mb: 1, color: "#0b0062", fontSize: "14px", fontWeight: 500 }}>
                  Tarifas seleccionadas:
                </Typography>
                <Box sx={{
                  maxHeight: "200px",
                  overflowY: "auto",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  p: 1
                }}>
                  {tarifasConStock?.map(tarifa => (
                    <Box
                      key={tarifa.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        p: 1,
                        borderBottom: "1px solid #f0f0f0",
                        "&:last-child": { borderBottom: "none" }
                      }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <Typography sx={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: "#333"
                        }}>
                          {tarifa.concepto}
                        </Typography>
                        <Typography sx={{
                          fontSize: "12px",
                          color: "#666"
                        }}>
                          ${tarifa.precio}
                        </Typography>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={tarifa.stock}
                              onChange={(e) => handleStockChange(tarifa.id, e.target.checked)}
                              size="small"
                              sx={{
                                color: '#5656FF',
                                '&.Mui-checked': {
                                  color: '#5656FF',
                                }
                              }}
                            />
                          }
                          label={
                            <Typography sx={{ fontSize: "12px", color: "#666" }}>
                              Stock
                            </Typography>
                          }
                          sx={{ margin: 0 }}
                        />

                        <IconButton
                          size="small"
                          onClick={() => handleRemoveTarifa(tarifa.id)}
                          sx={{
                            color: "#d32f2f",
                            "&:hover": { backgroundColor: "#ffebee" }
                          }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            <Box sx={{ mt: 2, p: 1.5, backgroundColor: "#f7f7ff", borderRadius: "8px", border: "1px solid #e0e0ff" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={esPreimpresa}
                    onChange={(e) => setEsPreimpresa(e.target.checked)}
                    size="small"
                    sx={{
                      color: '#5656FF',
                      '&.Mui-checked': { color: '#5656FF' }
                    }}
                  />
                }
                label={
                  <Typography sx={{ fontSize: "14px", color: "#0b0062", fontWeight: 500 }}>
                    Caja preimpresa
                  </Typography>
                }
                sx={{ margin: 0 }}
              />
              <Typography sx={{ fontSize: "12px", color: "#666", ml: 4 }}>
                Podés generar/administrar los tickets preimpresos de esta caja desde el menú de la caja.
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{
        padding: window.innerWidth <= 576 ? "12px" : "0 16px 16px",
        display: "flex",
        flexWrap: window.innerWidth <= 576 ? "wrap" : "nowrap",
        gap: "10px"
      }}>
        <Button sx={{
          border: "1px solid #5656FF",
          color: "#5656FF",
          padding: "5px 10px",
          borderRadius: "4px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: window.innerWidth <= 576 ? "13px" : "14px",
          width: "100%",
        }}
          onClick={handleClose}>Cancelar</Button>
        <Button
          sx={{
            marginLeft: "0 !important",
            backgroundColor: "#5656FF",
            color: "white",
            padding: "5px 10px",
            borderRadius: "4px",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: window.innerWidth <= 576 ? "13px" : "14px",
            width: "100%"
          }}
          variant="contained"
          onClick={handleSubmit}
          disabled={
            loading ||
            loadingPasarela ||
            loadingCaja ||
            !nombreCaja ||
            !vendedor ||
            (pasarela === "CLICK" && !cajaMacro)
          }
        >
          {loading ? <CircularProgress size={20} /> : "Guardar Cambios"}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default UpdateCajaModal;